// Client helper for the /api/extract-document Gemini function.
// Sends a captured photo, gets back structured fields the wizard pre-fills.
// The pilot always reviews/edits the result — assistive OCR, never authoritative.

export type ExtractableDocType =
  | 'pilot_certificate'
  | 'pilot_certificate_back'
  | 'medical'
  | 'drivers_license'
  | 'renters_insurance';

export interface ExtractedFields {
  // pilot_certificate
  certificateNumber?: string;
  certificateType?: string;
  certificateIssueDate?: string;
  ratings?: string[];
  // medical
  medicalClass?: string;
  medicalIssueDate?: string;
  medicalExpiration?: string;
  medicalLimitations?: string;
  // drivers_license
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  address?: string;
  licenseNumber?: string;
  // renters_insurance
  carrier?: string;
  policyNumber?: string;
  liabilityLimit?: number;
  // shared
  expirationDate?: string;
}

// BASE_URL is '/' on the app.ajmaviation.com subdomain, so this resolves to
// '/api/extract-document' — the function served by the same site. (Kept
// BASE_URL-relative so it survives any future base change.)
const EXTRACT_API = `${import.meta.env.BASE_URL}api/extract-document`;

// Walkable in demo mode (and a graceful fallback if the function is unreachable).
// Realistic-but-obviously-sample values so the confirm step has something to show.
const DEMO_FIELDS: Record<ExtractableDocType, ExtractedFields> = {
  pilot_certificate_back: { ratings: ['Airplane Single-Engine Land', 'Instrument Airplane'] },
  pilot_certificate: {
    certificateNumber: '3729104',
    certificateType: 'Private',
    certificateIssueDate: '2019-06-14',
    ratings: ['Airplane Single-Engine Land', 'Instrument Airplane'],
  },
  medical: {
    medicalClass: '3rd',
    medicalIssueDate: '2024-09-02',
    medicalExpiration: '2026-09-30',
    medicalLimitations: '',
  },
  drivers_license: {
    firstName: 'Demo',
    lastName: 'Lessee',
    dateOfBirth: '1986-03-21',
    address: '101 Main Street STE H, Seal Beach CA 90740',
    licenseNumber: 'D1234567',
    expirationDate: '2028-03-21',
  },
  renters_insurance: {
    carrier: 'Avemco',
    policyNumber: 'NR-884201',
    liabilityLimit: 1000000,
    expirationDate: '2026-12-31',
  },
};

function stripDataUrlPrefix(dataUrl: string): { base64: string; mimeType: string } {
  const match = /^data:([^;]+);base64,(.*)$/s.exec(dataUrl);
  if (match) return { mimeType: match[1], base64: match[2] };
  return { mimeType: 'image/jpeg', base64: dataUrl };
}

export interface ExtractResult {
  fields: ExtractedFields;
  source: 'ai' | 'demo' | 'manual';
  message?: string;
}

/**
 * Read a captured document photo. Resolves with extracted fields and a `source`
 * flag. Never throws — on any failure it returns empty fields with source
 * 'manual' so the wizard simply falls back to manual entry.
 */
export async function extractDocument(
  docType: ExtractableDocType,
  dataUrl: string,
  demoMode: boolean
): Promise<ExtractResult> {
  // In demo we still try the real reader (so it can be exercised under
  // `netlify dev`), but fall back to canned sample data if the function isn't
  // reachable (e.g. plain `vite`) instead of dropping to manual entry.
  const demoFallback: ExtractResult = { fields: DEMO_FIELDS[docType] ?? {}, source: 'demo' };

  const { base64, mimeType } = stripDataUrlPrefix(dataUrl);
  try {
    const res = await fetch(EXTRACT_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ docType, imageBase64: base64, mimeType }),
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data?.ok) {
      if (demoMode) return demoFallback;
      return {
        fields: {},
        source: 'manual',
        message: data?.message || 'Could not read the photo — enter the details below.',
      };
    }
    return { fields: (data.fields ?? {}) as ExtractedFields, source: 'ai' };
  } catch {
    if (demoMode) return demoFallback;
    return {
      fields: {},
      source: 'manual',
      message: 'Could not reach the document reader — enter the details below.',
    };
  }
}
